type Callback<D> = (data: D) => any;
type Unsubscribe = () => void;

type Handler<D> = {
    callback: Callback<D>;
    once: boolean;
};

export type TriggerOnOptions = {
    once?: boolean;
    signal?: AbortSignal;
};

export type TriggerAsyncOptions = {
    once?: boolean;
    signal?: AbortSignal;
};

export type Trigger<D = void> = {
    (data: D): void;
    triggerAsync(data: D, options?: TriggerAsyncOptions): Promise<any>;
    on(callback: Callback<D>, options?: TriggerOnOptions): Unsubscribe;
    off(callback: Callback<D>): void;
    clear(): void;
    name: string;
};

export function createTrigger<D=void>(callback?: Callback<D>, options?: TriggerOnOptions): Trigger<D> {
    const callbacks: Handler<D>[] = [];

    const result = function(data: D) {
        callbacks.forEach(item => {
            item.callback(data);
        });

        for (let i = 0; i < callbacks.length; i++) {
            if (callbacks[i].once) {
                callbacks.splice(i, 1);
                i--;
            }
        }
    } as Trigger<D>;

    result.triggerAsync = function(data: D): Promise<any> {
        const promises = callbacks.map(item => item.callback(data));

        for (let i = 0; i < callbacks.length; i++) {
            if (callbacks[i].once) {
                callbacks.splice(i, 1);
                i--;
            }
        }

        return Promise.all(promises);
    };

    result.on = function(callback: Callback<D>, options?: TriggerOnOptions): Unsubscribe {
        const off = () => result.off(callback);

        callbacks.push({
            callback,
            once: !!options?.once,
        });

        options?.signal?.addEventListener('abort', off);

        return off;
    };

    result.off = function(callback: Callback<D>): void {
        for (let i = 0; i < callbacks.length; i++) {
            if (callbacks[i].callback === callback) {
                callbacks.splice(i, 1);
                i--;
            }
        }
    };

    result.clear = function(): void {
        callbacks.length = 0;
    };

    if (callback) {
        result.on(callback, options);
    }

    return result;
}